import invariant from 'invariant';
import { InboxViews } from 'pages/Inbox/Inbox.config';
import { mapUrlToInboxViews } from 'pages/Inbox/Inbox.helpers';
import {
  all,
  call,
  debounce,
  delay,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';
import {
  apiRequestFailed,
  apiRequestSuccess,
  invalidateLoadedResourceIfPresent,
  requestDataIfNecessary,
} from 'services/api/api.actions';
import { createIsApiActionOf } from 'services/api/api.helpers';
import { unprocessableEntity_422 } from 'services/api/api.statusCodes';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import { formErrorKey, genericErrorId } from 'services/forms/forms.config';
import { createScopedErrorHandler } from 'utils/functions/handleError';
import mapValidationErrors from 'utils/functions/mapValidationErrors';
import {
  inboxUpdateCompleted,
  requestToDeleteSingleEmail,
  requestToRemoveAllInboxMessages,
  setInboxErrors,
  userHasReadMessage,
  userHasSeenMessages,
} from './inbox.action';
import {
  makeSelectApplyMessagePatchesApiConfig,
  makeSelectDeleteMessagesApiConfig,
  selectGetAllSgagInboxMessagesApiConfig,
  selectGetAmountOfMessagesApiConfig,
  selectInboxMessagesFromApi,
  selectListOfOptimisticPatches,
} from './inbox.selectors';
import { InboxMessage, ModifyMessageInfo } from './inbox.types';

const handleInboxSagaError = createScopedErrorHandler('inbox-saga');

function matchPath(pathname: string, { path, exact }: any) {
  const regex = new RegExp(
    `^${path.replace(/:\w+/g, '([^/]+)')}${exact ? '$' : ''}`,
  );
  const match = pathname.match(regex);

  if (!match) return null;

  // Extract dynamic segments (e.g., :id)
  const params = {};
  const paramNames = (path.match(/:\w+/g) || []).map((param) => param.slice(1));

  paramNames.forEach((name, index) => {
    params[name] = match[index + 1];
  });

  return { params };
}

function* handleBlockingUpdateCompleted(
  action: ReturnType<typeof inboxUpdateCompleted>,
) {
  const routesFromInboxMessagePageToBasePage = () => {
    const output = matchPath(window.location.pathname, {
      exact: false,
      path: '/inbox',
    });

    if (!output) {
      return;
    }

    const parsedQueryParams = Object.fromEntries(
      new URLSearchParams(window.location.search).entries(),
    );

    const inboxUrlState = mapUrlToInboxViews(parsedQueryParams);

    if (inboxUrlState.inboxView === InboxViews.showMessage) {
      window.location.replace('/inbox');
    }
  };

  yield routesFromInboxMessagePageToBasePage();
}

function* updateInboxData() {
  const requestAllMessagesApiConfig: RequestApiDataIfNecessaryPayload =
    yield select(selectGetAllSgagInboxMessagesApiConfig);

  const requestAmountOfMessagesApiConfig: RequestApiDataIfNecessaryPayload =
    yield select(selectGetAmountOfMessagesApiConfig);

  yield put(requestDataIfNecessary(requestAmountOfMessagesApiConfig));
  yield put(requestDataIfNecessary(requestAllMessagesApiConfig));

  // Await the message update before yielding control to its creator.
  yield all([
    take([
      createIsApiActionOf(
        apiRequestSuccess,
        requestAllMessagesApiConfig.resourceId,
      ),
      createIsApiActionOf(
        apiRequestFailed,
        requestAllMessagesApiConfig.resourceId,
      ),
    ]),
    take([
      createIsApiActionOf(
        apiRequestSuccess,
        requestAmountOfMessagesApiConfig.resourceId,
      ),
      createIsApiActionOf(
        apiRequestFailed,
        requestAmountOfMessagesApiConfig.resourceId,
      ),
    ]),
  ]);
}

function* flushOptimisticUIUpdates() {
  const listOfPatches: ModifyMessageInfo[] = yield select(
    selectListOfOptimisticPatches,
  );

  if (listOfPatches.length < 1) {
    return;
  }

  const bulkEditMessageInfoApiConfig: RequestApiDataIfNecessaryPayload =
    yield select(makeSelectApplyMessagePatchesApiConfig(listOfPatches));

  const bulkEditResourceId = bulkEditMessageInfoApiConfig.resourceId;

  yield put(
    invalidateLoadedResourceIfPresent({
      resourceId: bulkEditMessageInfoApiConfig.resourceId,
    }),
  );
  yield put(requestDataIfNecessary(bulkEditMessageInfoApiConfig));

  // Await the completion before updating.
  yield take([
    createIsApiActionOf(apiRequestSuccess, bulkEditResourceId),
    createIsApiActionOf(apiRequestFailed, bulkEditResourceId),
  ]);

  yield call(updateInboxData);
}

function* handleUIBlockingUpdates(apiConfig: RequestApiDataIfNecessaryPayload) {
  const { resourceId: requestedActionId } = apiConfig;

  yield put(
    invalidateLoadedResourceIfPresent({ resourceId: requestedActionId }),
  );
  yield put(requestDataIfNecessary(apiConfig));

  const { failure, success } = yield race({
    success: take(createIsApiActionOf(apiRequestSuccess, requestedActionId)),
    failure: take(createIsApiActionOf(apiRequestFailed, requestedActionId)),
  });

  if (failure) {
    invariant(
      apiRequestFailed.match(failure),
      `inbox: ${failure} is not an 'apiRequestFailed' action`,
    );

    const {
      payload: { error },
    } = failure;

    let errors: Record<string, string> = { [formErrorKey]: genericErrorId };

    if (error?.response?.status === unprocessableEntity_422) {
      const computedErrors = mapValidationErrors(error.response, {});

      if (computedErrors) {
        errors = computedErrors;
      }
    }
    yield put(setInboxErrors(errors));
  } else {
    invariant(
      apiRequestSuccess.match(success),
      `inbox: ${success} is not an 'apiRequestSuccess' action`,
    );

    yield call(updateInboxData);

    // Delay a bit to give time to re-render.
    yield delay(200);
  }
}

function* handleDeleteSingleMessageRequest(
  action: ReturnType<typeof requestToDeleteSingleEmail>,
) {
  try {
    const requestEmailRemovalApiConfig: RequestApiDataIfNecessaryPayload =
      yield select(makeSelectDeleteMessagesApiConfig([action.payload]));

    yield call(handleUIBlockingUpdates, requestEmailRemovalApiConfig);
  } catch (sagaError) {
    handleInboxSagaError(sagaError);
  } finally {
    yield put(inboxUpdateCompleted('deleteSingleMessage'));
  }
}

function* handleDeleteAllMessagesRequest(
  action: ReturnType<typeof requestToRemoveAllInboxMessages>,
) {
  try {
    const allInboxMessages: InboxMessage[] = Object.values(
      yield select(selectInboxMessagesFromApi),
    );

    if (allInboxMessages.length < 1) {
      return;
    }

    const deleteAllMessagesApiConfig: RequestApiDataIfNecessaryPayload =
      yield select(makeSelectDeleteMessagesApiConfig(allInboxMessages));

    yield call(handleUIBlockingUpdates, deleteAllMessagesApiConfig);
  } catch (sagaError) {
    handleInboxSagaError(sagaError);
  } finally {
    yield put(inboxUpdateCompleted('deleteAllMessages'));
  }
}

export default function* rootInboxSaga() {
  yield all([
    takeEvery(inboxUpdateCompleted.type, handleBlockingUpdateCompleted),
    takeEvery(
      requestToDeleteSingleEmail.type,
      handleDeleteSingleMessageRequest,
    ),
    takeLeading(
      requestToRemoveAllInboxMessages.type,
      handleDeleteAllMessagesRequest,
    ),
    takeEvery(userHasSeenMessages.type, flushOptimisticUIUpdates),
    debounce(3_000, userHasReadMessage.type, flushOptimisticUIUpdates),
  ]);
}
